"use strict";
// Copyright (c) Jupyter Development Team.
// Distributed under the terms of the Modified BSD License.
Object.defineProperty(exports, "__esModule", { value: true });
var disposable_1 = require("@phosphor/disposable");
var signaling_1 = require("@phosphor/signaling");
var coreutils_1 = require("@phosphor/coreutils");
var observablemap_1 = require("./observablemap");
var observablejson_1 = require("./observablejson");
var observablestring_1 = require("./observablestring");
var undoablelist_1 = require("./undoablelist");
/**
 * A concrete implementation of an `IObservableValue`.
 */
var ObservableValue = /** @class */ (function () {
    /**
     * Constructor for the value.
     *
     * @param initialValue: the starting value for the `ObservableValue`.
     */
    function ObservableValue(initialValue) {
        if (initialValue === void 0) { initialValue = null; }
        this._value = null;
        this._changed = new signaling_1.Signal(this);
        this._isDisposed = false;
        this._value = initialValue;
    }
    Object.defineProperty(ObservableValue.prototype, "type", {
        /**
         * The observable type.
         */
        get: function () {
            return 'Value';
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ObservableValue.prototype, "isDisposed", {
        /**
         * Whether the value has been disposed.
         */
        get: function () {
            return this._isDisposed;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ObservableValue.prototype, "changed", {
        /**
         * The changed signal.
         */
        get: function () {
            return this._changed;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Get the current value, or `undefined` if it has not been set.
     */
    ObservableValue.prototype.get = function () {
        return this._value;
    };
    /**
     * Set the current value.
     */
    ObservableValue.prototype.set = function (value) {
        var oldValue = this._value;
        if (coreutils_1.JSONExt.deepEqual(oldValue, value)) {
            return;
        }
        this._value = value;
        this._changed.emit({
            oldValue: oldValue,
            newValue: value
        });
    };
    /**
     * Dispose of the resources held by the value.
     */
    ObservableValue.prototype.dispose = function () {
        if (this._isDisposed) {
            return;
        }
        this._isDisposed = true;
        signaling_1.Signal.clearData(this);
        this._value = null;
    };
    return ObservableValue;
}());
exports.ObservableValue = ObservableValue;
/**
 * The namespace for the `ObservableValue` class statics.
 */
(function (ObservableValue) {
    /**
     * The changed args object emitted by the `IObservableValue`.
     */
    var IChangedArgs = /** @class */ (function () {
        function IChangedArgs() {
        }
        return IChangedArgs;
    }());
    ObservableValue.IChangedArgs = IChangedArgs;
})(ObservableValue = exports.ObservableValue || (exports.ObservableValue = {}));
exports.ObservableValue = ObservableValue;
/**
 * A concrete implementation of an `IModelDB`.
 */
var ModelDB = /** @class */ (function () {
    /**
     * Constructor for the `ModelDB`.
     */
    function ModelDB(options) {
        if (options === void 0) { options = {}; }
        /**
         * Whether the model has been populated with
         * any model values.
         */
        this.isPrepopulated = false;
        /**
         * Whether the model is collaborative.
         */
        this.isCollaborative = false;
        /**
         * A promise resolved when the model is connected
         * to its backend. For the in-memory ModelDB it
         * is immediately resolved.
         */
        this.connected = Promise.resolve(void 0);
        this._toDispose = false;
        this._isDisposed = false;
        this._disposables = new disposable_1.DisposableSet();
        this._basePath = options.basePath || '';
        if (options.baseDB) {
            this._db = options.baseDB;
        }
        else {
            this._db = new observablemap_1.ObservableMap();
            this._toDispose = true;
        }
    }
    Object.defineProperty(ModelDB.prototype, "basePath", {
        /**
         * The base path for the `ModelDB`. This is prepended
         * to all the paths that are passed in to the member
         * functions of the object.
         */
        get: function () {
            return this._basePath;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ModelDB.prototype, "isDisposed", {
        /**
         * Whether the database is disposed.
         */
        get: function () {
            return this._isDisposed;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Get a value for a path.
     *
     * @param path: the path for the object.
     *
     * @returns an `IObservable`.
     */
    ModelDB.prototype.get = function (path) {
        return this._db.get(this._resolvePath(path));
    };
    /**
     * Whether the `IModelDB` has an object at this path.
     *
     * @param path: the path for the object.
     *
     * @returns a boolean for whether an object is at `path`.
     */
    ModelDB.prototype.has = function (path) {
        return this._db.has(this._resolvePath(path));
    };
    /**
     * Create a string and insert it in the database.
     *
     * @param path: the path for the string.
     *
     * @returns the string that was created.
     */
    ModelDB.prototype.createString = function (path) {
        var str = new observablestring_1.ObservableString();
        this._disposables.add(str);
        this.set(path, str);
        return str;
    };
    /**
     * Create an undoable list and insert it in the database.
     *
     * @param path: the path for the list.
     *
     * @returns the list that was created.
     *
     * #### Notes
     * The list can only store objects that are simple
     * JSON Objects and primitives.
     */
    ModelDB.prototype.createList = function (path) {
        var vec = new undoablelist_1.ObservableUndoableList(new undoablelist_1.ObservableUndoableList.IdentitySerializer());
        this._disposables.add(vec);
        this.set(path, vec);
        return vec;
    };
    /**
     * Create a map and insert it in the database.
     *
     * @param path: the path for the map.
     *
     * @returns the map that was created.
     *
     * #### Notes
     * The map can only store objects that are simple
     * JSON Objects and primitives.
     */
    ModelDB.prototype.createMap = function (path) {
        var map = new observablejson_1.ObservableJSON();
        this._disposables.add(map);
        this.set(path, map);
        return map;
    };
    /**
     * Create an opaque value and insert it in the database.
     *
     * @param path: the path for the value.
     *
     * @returns the value that was created.
     */
    ModelDB.prototype.createValue = function (path) {
        var val = new ObservableValue();
        this._disposables.add(val);
        this.set(path, val);
        return val;
    };
    /**
     * Get a value at a path, or `undefined if it has not been set
     * That value must already have been created using `createValue`.
     *
     * @param path: the path for the value.
     */
    ModelDB.prototype.getValue = function (path) {
        var val = this.get(path);
        if (!val || val.type !== 'Value') {
            throw Error('Can only call getValue for an ObservableValue');
        }
        return val.get();
    };
    /**
     * Set a value at a path. That value must already have
     * been created using `createValue`.
     *
     * @param path: the path for the value.
     *
     * @param value: the new value.
     */
    ModelDB.prototype.setValue = function (path, value) {
        var val = this.get(path);
        if (!val || val.type !== 'Value') {
            throw Error('Can only call setValue on an ObservableValue');
        }
        val.set(value);
    };
    /**
     * Create a view onto a subtree of the model database.
     *
     * @param basePath: the path for the root of the subtree.
     *
     * @returns an `IModelDB` with a view onto the original
     *   `IModelDB`, with `basePath` prepended to all paths.
     */
    ModelDB.prototype.view = function (basePath) {
        var view = new ModelDB({ basePath: basePath, baseDB: this });
        this._disposables.add(view);
        return view;
    };
    /**
     * Set a value at a path. Not intended to
     * be called by user code, instead use the
     * `create*` factory methods.
     *
     * @param path: the path to set the value at.
     *
     * @param value: the value to set at the path.
     */
    ModelDB.prototype.set = function (path, value) {
        this._db.set(this._resolvePath(path), value);
    };
    /**
     * Dispose of the resources held by the database.
     */
    ModelDB.prototype.dispose = function () {
        if (this.isDisposed) {
            return;
        }
        this._isDisposed = true;
        if (this._toDispose) {
            this._db.dispose();
        }
        this._disposables.dispose();
    };
    /**
     * Compute the fully resolved path for a path argument.
     */
    ModelDB.prototype._resolvePath = function (path) {
        if (this._basePath) {
            path = this._basePath + '.' + path;
        }
        return path;
    };
    return ModelDB;
}());
exports.ModelDB = ModelDB;
